import { useState } from "react";
import { Marker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

export const SHIP_CATEGORIES = [
  { key: "passenger", label: "Passenger", colour: "#3B76EE" },
  { key: "cargo", label: "Cargo", colour: "#36A56A" },
  { key: "tanker", label: "Tanker", colour: "#E2554E" },
  { key: "hsc", label: "High speed", colour: "#EFB12E" },
  { key: "fishing", label: "Fishing", colour: "#F08537" },
  { key: "service", label: "Tugs & service", colour: "#8C6AF0" },
  { key: "pleasure", label: "Sailing & pleasure", colour: "#E96BB4" },
  { key: "other", label: "Other", colour: "#8796AC" },
];
const BY_KEY = Object.fromEntries(SHIP_CATEGORIES.map((c) => [c.key, c]));

export function shipCategory(code) {
  if (code >= 60 && code <= 69) return BY_KEY.passenger;
  if (code >= 70 && code <= 79) return BY_KEY.cargo;
  if (code >= 80 && code <= 89) return BY_KEY.tanker;
  if (code >= 40 && code <= 49) return BY_KEY.hsc;
  if (code === 30) return BY_KEY.fishing;
  if ((code >= 31 && code <= 35) || (code >= 50 && code <= 59)) return BY_KEY.service;
  if (code === 36 || code === 37) return BY_KEY.pleasure;
  return BY_KEY.other;
}

export const isMoving = (p) => (p?.sog_knots ?? 0) >= 0.5;

const bearing = (p) => (p.heading_deg != null && p.heading_deg < 360 ? p.heading_deg : p.cog_deg ?? 0);

function shipIcon(v, zoom, selected, stale) {
  const p = v.last_position;
  const { colour } = shipCategory(v.ship_type_code);
  const s = zoom >= 15 ? 30 : zoom >= 12 ? 20 : zoom >= 9 ? 14 : 9;
  const moving = isMoving(p);
  const fill = stale ? "#5b6676" : colour;
  const hull = moving ? "M10 1 L16 9 L15 23 L5 23 L4 9 Z" : "M10 2 C14 2 15 6 15 9 L15 21 C15 22.5 13.5 23 12 23 L8 23 C6.5 23 5 22.5 5 21 L5 9 C5 6 6 2 10 2 Z";
  const ring = selected ? `<circle cx="10" cy="12" r="11" fill="none" stroke="#5EEAD4" stroke-width="1.6"/>` : "";
  return L.divIcon({
    className: "",
    iconSize: [s, s * 1.2],
    iconAnchor: [s / 2, s * 0.6],
    html: `<div style="width:${s}px;height:${s * 1.2}px;transform:rotate(${moving || p.heading_deg != null ? bearing(p) : 0}deg);filter:drop-shadow(0 0 2px rgba(0,0,0,.8))"><svg width="${s}" height="${s * 1.2}" viewBox="0 0 20 24">${ring}<path d="${hull}" fill="${fill}" stroke="${selected ? "#ffffff" : "rgba(10,14,20,.9)"}" stroke-width="1.1"/></svg></div>`,
  });
}

export function ShipLayer({ vessels, selected, onSelect }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [, setMoved] = useState(0);
  useMapEvents({ zoomend: () => setZoom(map.getZoom()), moveend: () => setMoved((n) => n + 1) });
  const labelled = new Set();
  if (zoom >= 16) {
    const placed = [];
    [...vessels].sort((a, b) => (a.mmsi === selected ? -1 : b.mmsi === selected ? 1 : 0)).forEach((v) => {
      const pt = map.latLngToContainerPoint([v.last_position.lat, v.last_position.lon]);
      if (placed.every((q) => Math.abs(q.x - pt.x) > 70 || Math.abs(q.y - pt.y) > 16)) { placed.push(pt); labelled.add(v.mmsi); }
    });
  }
  return vessels.map((v) => (
    <Marker key={v.mmsi} position={[v.last_position.lat, v.last_position.lon]} icon={shipIcon(v, zoom, selected === v.mmsi, v.freshness.status !== "OK")} zIndexOffset={selected === v.mmsi ? 1000 : 0} eventHandlers={{ click: () => onSelect(v.mmsi) }}>
      {labelled.has(v.mmsi) ? <Tooltip key="label" permanent direction="bottom" offset={[0, 6]} className="ship-label" opacity={1}>{v.name || v.mmsi}</Tooltip> : <Tooltip key="hover" direction="top" className="ship-label" opacity={1}>{v.name || `MMSI ${v.mmsi}`}</Tooltip>}
    </Marker>
  ));
}

export function ShipLegend({ vessels }) {
  const counts = {};
  vessels.forEach((v) => { const k = shipCategory(v.ship_type_code).key; counts[k] = (counts[k] || 0) + 1; });
  return (
    <div data-testid="ship-legend" className="flex flex-wrap gap-1.5">
      {SHIP_CATEGORIES.map((c) => (
        <span key={c.key} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-2 py-0.5 text-[11px] text-slate-300">
          <i className="h-2 w-2 rounded-full" style={{ background: c.colour }} />{c.label}<b className="font-mono text-slate-100">{counts[c.key] || 0}</b>
        </span>
      ))}
    </div>
  );
}
